/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import DataTable from 'src/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from 'src/components/ui/dialog';
import { StatusPill } from 'src/components/ui/status-pill';
import { useToast } from 'src/hooks/use-toast';
import { PartnerService } from 'src/services/api/PartnerService';
import { useCreatePartner } from 'src/services/api/mutations/partnerMutations';
import { PartnerModal } from 'src/components/Modals/PartnerModal';
import AppSelect from 'src/components/common/AppSelect';

const DEFAULT_PAGE_SIZE = 10;

const PartnerTypeBadge = ({ value }) => {
  if (value === 1 || value === '1') {
    return (
      <StatusPill
        name="Partner"
        textColor="indigo-600"
        borderColor="indigo-200"
        bgColor="indigo-50"
      />
    );
  }
  if (value === 2 || value === '2') {
    return (
      <StatusPill
        name="Institution"
        textColor="violet-700"
        borderColor="violet-200"
        bgColor="violet-50"
      />
    );
  }
  return <span className="text-muted-foreground text-xs">Unknown</span>;
};

const StatusCell = ({ value }) => {
  const isActive = Boolean(value);
  const name = isActive ? 'Active' : 'Inactive';
  const textColor = isActive ? 'emerald-600' : 'red-600';
  const borderColor = isActive ? 'emerald-200' : 'red-200';
  const bgColor = isActive ? 'emerald-50' : 'red-50';
  return (
    <StatusPill
      name={name}
      textColor={textColor}
      borderColor={borderColor}
      bgColor={bgColor}
    />
  );
};

const createColumns = () => [
  {
    key: 'name',
    header: 'Partner Name',
    minWidth: '240px',
    tooltip: true,
  },
  {
    key: 'partner_type',
    header: 'Type',
    width: '160px',
    minWidth: '140px',
    render: (value) => <PartnerTypeBadge value={value} />,
  },
  {
    key: 'created_at',
    header: 'Created At',
    width: '180px',
    minWidth: '160px',
    render: (value) =>
      value
        ? new Date(value).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : '—',
  },
  {
    key: 'is_active',
    header: 'Status',
    width: '120px',
    minWidth: '110px',
    render: (value) => <StatusCell value={value} />,
  },
];

function Partners({ isUserManagement = false }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );
  const [totalItems, setTotalItems] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    partnerType: 'all',
    includeInactive: false,
  });

  const createPartnerMutation = useCreatePartner();
  const columns = createColumns();

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const fetchPartners = async (page, size) => {
    try {
      setIsLoading(true);
      const response = await PartnerService.listPartners({
        page,
        pageSize: size,
        search: filters.search,
        partnerType:
          filters.partnerType === 'all' ? undefined : filters.partnerType,
        includeInactive: filters.includeInactive,
      });
      const partnersData = response?.partners || [];
      const pagination = response?.pagination || {};
      const totalCount =
        pagination.total ??
        pagination.total_items ??
        (Array.isArray(partnersData) ? partnersData.length : 0);

      setPartners(partnersData);
      setTotalItems(totalCount);
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast({
        title: 'Error',
        description: 'Failed to load partners. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set('pageSize', DEFAULT_PAGE_SIZE.toString());
      setSearchParams(updatedParams);
    }
  }, []);

  const handlePageChange = (page) => {
    updateUrlParams({ page });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: newPageSize, page: 1 });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleCreate = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      await createPartnerMutation.mutateAsync(formData);
      toast({
        title: 'Success',
        description: 'Partner created successfully.',
      });
      handleModalClose();
      fetchPartners(currentPage, pageSize);
    } catch (error) {
      console.error('Error creating partner:', error);
      toast({
        title: 'Error',
        description: 'Failed to create partner.',
        variant: 'destructive',
      });
    }
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    updateUrlParams({ page: 1 });
  };

  const handlePartnerTypeChange = (value) => {
    setFilters((prev) => ({ ...prev, partnerType: value }));
    updateUrlParams({ page: 1 });
  };

  const handleIncludeInactiveChange = (checked) => {
    setFilters((prev) => ({ ...prev, includeInactive: checked }));
    updateUrlParams({ page: 1 });
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  return (
    <div>
      <PageHeader
        title="Partners"
        actions={
          !isUserManagement && (
            <button
              type="button"
              onClick={handleCreate}
              className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-[border-color,color,background-color] cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
              Create Partner
            </button>
          )
        }
      />

      <PageContainer>
        {/* Filter bar */}
        <div className="flex items-center gap-2.5 mb-4">
          <div className="relative">
            <Search
              className="absolute left-[11px] top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-muted-foreground pointer-events-none"
              strokeWidth={1.8}
            />
            <input
              type="text"
              value={filters.search}
              onChange={handleSearchChange}
              placeholder="Search by name or alias..."
              disabled={isLoading}
              className="pl-[34px] pr-3 py-2 rounded-lg border border-input text-[12.5px] text-foreground-strong bg-card outline-none w-[260px] transition-[border-color,box-shadow] placeholder:text-muted-foreground focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div className="flex items-center gap-2.5 ml-auto">
            <AppSelect
              value={filters.partnerType}
              onValueChange={handlePartnerTypeChange}
              disabled={isLoading}
              options={[
                { value: 'all', label: 'All partner types' },
                { value: '1', label: 'Partner' },
                { value: '2', label: 'Institution' },
              ]}
              className="h-auto py-2 text-[12.5px] font-[500] w-[180px]"
            />

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <button
                type="button"
                role="switch"
                aria-checked={filters.includeInactive}
                onClick={() =>
                  !isLoading &&
                  handleIncludeInactiveChange(!filters.includeInactive)
                }
                disabled={isLoading}
                className={`relative inline-flex w-9 h-5 rounded-full transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                  filters.includeInactive ? 'bg-primary' : 'bg-input'
                }`}
              >
                <span
                  className={`absolute top-[3px] left-[3px] w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-transform ${
                    filters.includeInactive ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-[12.5px] font-[500] text-muted-foreground whitespace-nowrap">
                Include inactive
              </span>
            </label>
          </div>
        </div>

        <DataTable
          className="border-border shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
          data={partners}
          columns={columns}
          loading={isLoading}
          tableLayout="auto"
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyState="No partners found."
          rowClassName={() => 'text-sm'}
          onRowClick={(row) =>
            !isUserManagement
              ? navigate(`/partners/${row.org_id}`)
              : navigate(`/partner-users/${row.org_id}`)
          }
        />
      </PageContainer>

      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent
          className="sm:max-w-[560px] p-0 gap-0 rounded-xl border-border shadow-[0_20px_60px_rgba(0,0,0,0.18),0_2px_8px_rgba(0,0,0,0.08)] flex flex-col"
          style={{ maxHeight: '90vh' }}
        >
          <div className="px-6 pt-5 pb-0 flex-shrink-0">
            <DialogTitle className="text-[16px] font-bold text-foreground-strong tracking-[-0.02em]">
              Create Partner
            </DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground mt-[3px]">
              Add a new partner organisation or institution
            </DialogDescription>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <PartnerModal
              key={isModalOpen ? 'open' : 'closed'}
              onSubmit={handleSubmit}
              onCancel={handleModalClose}
              loading={createPartnerMutation.isPending}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Partners;
